import { useEffect, useMemo, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Drawer,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  AuditOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { useIsMobile } from '@/lib/hooks'
import {
  BadgeIconUpload,
  pathForSavedBadgeIcon,
  prepareBadgeIconForSave,
  type BadgeIconValue,
} from '@/components/BadgeIconUpload'
import {
  BADGE_PAGE_SIZE,
  ELIGIBILITY_KIND_LABELS,
  badgeDefinitionListKey,
  badgeDefinitionOptionsKey,
  describeSetActiveResult,
  formatBadgeError,
  isSeedPlaceholderIcon,
  listBadgeDefinitions,
  setBadgeDefinitionActive,
  upsertBadgeDefinition,
  type BadgeActiveFilter,
  type BadgeDefinition,
} from '@/lib/badges'
import {
  badgeIconPublicUrl,
  formatBadgeIconError,
  formatPublishedIconError,
  uploadBadgeIcon,
  verifyPublishedBadgeIcon,
  type BadgeIconValidationError,
} from '@/lib/badge-icons'
import { formatCST } from '@/lib/time'

export const Route = createFileRoute('/_authed/badges')({
  component: BadgesPage,
})

type EditorState = 'new' | BadgeDefinition

interface DefinitionFormValues {
  code: string
  name: string
  description: string
  sort_order: number
  reason: string
}

function BadgesPage() {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const [active, setActive] = useState<BadgeActiveFilter>('all')
  const [page, setPage] = useState(1)
  const [editing, setEditing] = useState<EditorState | null>(null)
  const [iconValue, setIconValue] = useState<BadgeIconValue | null>(null)
  const [form] = Form.useForm<DefinitionFormValues>()
  const [activeTarget, setActiveTarget] = useState<{
    row: BadgeDefinition
    isActive: boolean
  } | null>(null)
  const [activeReason, setActiveReason] = useState('')
  const isMobile = useIsMobile()

  useEffect(() => {
    setPage(1)
  }, [active])

  const listQuery = useQuery({
    queryKey: badgeDefinitionListKey({
      active,
      page,
      pageSize: BADGE_PAGE_SIZE,
    }),
    placeholderData: keepPreviousData,
    queryFn: () =>
      listBadgeDefinitions({
        active,
        limit: BADGE_PAGE_SIZE,
        offset: (page - 1) * BADGE_PAGE_SIZE,
      }),
  })

  const rows = listQuery.data?.rows ?? []
  const total = listQuery.data?.total ?? 0

  async function refreshCatalog() {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['badge_definitions'] }),
      qc.invalidateQueries({ queryKey: badgeDefinitionOptionsKey }),
      qc.invalidateQueries({ queryKey: ['user_badge_grants'] }),
      qc.invalidateQueries({ queryKey: ['moderation_logs'] }),
    ])
  }

  async function afterWrite(successMessage: string) {
    try {
      await refreshCatalog()
      message.success(successMessage)
    } catch {
      message.warning('操作已完成，刷新失败')
    }
  }

  const saveMutation = useMutation({
    mutationFn: async (values: DefinitionFormValues) => {
      const current = editing
      if (current == null) throw new Error('not_found')
      const prepared = await prepareBadgeIconForSave(iconValue, (file) =>
        uploadBadgeIcon(file),
      )
      if (!prepared.ok) {
        throw new Error(prepared.error)
      }
      setIconValue(prepared.nextValue)
      const isNew = current === 'new'
      return {
        isNew,
        result: await upsertBadgeDefinition(
          isNew
            ? {
                code: values.code,
                name: values.name,
                description: values.description,
                iconPath: prepared.path,
                sortOrder: values.sort_order,
                reason: values.reason,
              }
            : {
                id: current.id,
                name: values.name,
                description: values.description,
                iconPath: prepared.path,
                sortOrder: values.sort_order,
                reason: values.reason,
              },
        ),
      }
    },
    onSuccess: async ({ isNew }) => {
      setEditing(null)
      setIconValue(null)
      form.resetFields()
      await afterWrite(isNew ? '已创建，当前为禁用状态' : '徽章已更新')
    },
    onError: (error: unknown) => {
      const code = error instanceof Error ? error.message : 'unknown'
      if (
        code === 'file_required' ||
        code === 'file_too_large' ||
        code === 'unsupported_type' ||
        code === 'invalid_dimensions' ||
        code === 'decode_failed' ||
        code === 'upload_failed'
      ) {
        message.error(
          code === 'upload_failed'
            ? '图标上传失败，定义未保存'
            : formatBadgeIconError(code as BadgeIconValidationError),
        )
        return
      }
      message.error(formatBadgeError(error))
    },
  })

  const activeMutation = useMutation({
    mutationFn: async (input: {
      row: BadgeDefinition
      isActive: boolean
      reason: string
    }) => {
      if (input.isActive) {
        const verified = await verifyPublishedBadgeIcon(input.row.icon_path)
        if (!verified.ok) {
          throw new Error(verified.error)
        }
      }
      return setBadgeDefinitionActive({
        badgeId: input.row.id,
        isActive: input.isActive,
        reason: input.reason,
      })
    },
    onSuccess: async (result, vars) => {
      setActiveTarget(null)
      setActiveReason('')
      await afterWrite(describeSetActiveResult(vars.isActive, result.unchanged))
    },
    onError: (error: unknown) => {
      const code = error instanceof Error ? error.message : 'unknown'
      if (
        code === 'icon_unreadable' ||
        code === 'invalid_icon_path' ||
        code === 'file_too_large' ||
        code === 'unsupported_type' ||
        code === 'invalid_dimensions' ||
        code === 'decode_failed'
      ) {
        message.error(formatPublishedIconError(code))
        return
      }
      message.error(formatBadgeError(error))
    },
  })

  function openCreate() {
    setEditing('new')
    setIconValue(null)
    form.resetFields()
    form.setFieldsValue({ sort_order: 0 })
  }

  function openEdit(row: BadgeDefinition) {
    setEditing(row)
    setIconValue({ kind: 'existing', path: row.icon_path })
    form.setFieldsValue({
      code: row.code,
      name: row.name,
      description: row.description,
      sort_order: row.sort_order,
      reason: undefined,
    })
  }

  const columns: ColumnsType<BadgeDefinition> = useMemo(
    () => [
      {
        title: '图标',
        dataIndex: 'icon_path',
        width: 72,
        render: (path: string, row) => (
          <BadgeThumb path={path} name={row.name} />
        ),
      },
      {
        title: '名称',
        dataIndex: 'name',
        render: (name: string, row) => (
          <Space orientation="vertical" size={0}>
            <Typography.Text strong>{name}</Typography.Text>
            <Typography.Text
              type="secondary"
              copyable
              style={{ fontFamily: 'var(--ant-font-family-code, monospace)' }}
            >
              {row.code}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '说明',
        dataIndex: 'description',
        ellipsis: true,
      },
      {
        title: '资格来源',
        dataIndex: 'eligibility_kind',
        width: 132,
        render: (kind: BadgeDefinition['eligibility_kind']) =>
          ELIGIBILITY_KIND_LABELS[kind],
      },
      {
        title: '状态',
        dataIndex: 'is_active',
        width: 88,
        render: (activeFlag: boolean) => (
          <Tag color={activeFlag ? 'green' : 'default'}>
            {activeFlag ? '启用' : '禁用'}
          </Tag>
        ),
      },
      {
        title: '排序',
        dataIndex: 'sort_order',
        width: 72,
      },
      {
        title: '更新时间',
        dataIndex: 'updated_at',
        width: 168,
        render: (value: string) => formatCST(value),
      },
      {
        title: '操作',
        key: 'actions',
        width: 220,
        fixed: 'right',
        render: (_, row) => (
          <Space size={4} wrap>
            <Tooltip title="编辑">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => openEdit(row)}
              />
            </Tooltip>
            <Button
              size="small"
              danger={row.is_active}
              onClick={() => {
                setActiveReason('')
                setActiveTarget({ row, isActive: !row.is_active })
              }}
            >
              {row.is_active ? '禁用' : '启用'}
            </Button>
            <Link
              to="/audit"
              search={{
                target_type: 'badge_definition',
                target_id: row.id,
              }}
            >
              <Button type="text" size="small" icon={<AuditOutlined />}>
                审计
              </Button>
            </Link>
          </Space>
        ),
      },
    ],
    [],
  )

  const seedWarning = rows.some((row) => isSeedPlaceholderIcon(row.icon_path))

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space
          align="center"
          style={{ width: '100%', justifyContent: 'space-between' }}
          wrap
        >
          <Space orientation="vertical" size={4}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              徽章
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              维护徽章目录。新建后默认禁用；启用、禁用与授予都需要填写原因。定义只能停用，不能删除。
            </Typography.Paragraph>
          </Space>
          <Space wrap className="badges-toolbar">
            <Segmented
              block={isMobile}
              value={active}
              options={[
                { value: 'all', label: '全部' },
                { value: 'active', label: '启用' },
                { value: 'inactive', label: '禁用' },
              ]}
              onChange={(value) => setActive(value as BadgeActiveFilter)}
            />
            <Tooltip title={isMobile ? '刷新' : undefined}>
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                loading={listQuery.isFetching}
                onClick={() => void listQuery.refetch()}
              >
                {isMobile ? null : '刷新'}
              </Button>
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              新建徽章
            </Button>
          </Space>
        </Space>
      </Card>

      {seedWarning ? (
        <Alert
          type="warning"
          showIcon
          title="预置徽章尚未完成发布检查"
          description="community_admin、permanent_member、question_contributor 三枚预置徽章默认禁用，且仍使用待上传的占位图标路径。请先替换为真实 96×96 PNG/WebP 并保存，再逐枚启用，不要批量启用。"
        />
      ) : null}

      {listQuery.isError ? (
        <Alert
          type="error"
          showIcon
          title="徽章目录加载失败"
          description={formatBadgeError(listQuery.error)}
          action={
            <Button onClick={() => void listQuery.refetch()}>重试</Button>
          }
        />
      ) : null}

      <Card>
        <div className="admin-table-wrap">
          <div className="admin-desktop-table">
            <Table
              rowKey="id"
              columns={columns}
              dataSource={rows}
              loading={listQuery.isLoading}
              scroll={{ x: 1100 }}
              pagination={{
                current: page,
                pageSize: BADGE_PAGE_SIZE,
                total,
                showTotal: (count) => `共 ${count} 枚`,
                onChange: setPage,
              }}
            />
          </div>
        </div>
        {/* 手机卡片：admin-table-wrap 在 <992 隐藏，必须放在它外面 */}
        <AdminMobileList<BadgeDefinition>
          rows={rows}
          rowKey={(row) => row.id}
          loading={listQuery.isLoading || listQuery.isFetching}
          page={page}
          pageSize={BADGE_PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          renderItem={(row) => (
            <article
              className="admin-mobile-record"
              role="button"
              tabIndex={0}
              aria-label={`编辑徽章 ${row.name}`}
              onClick={() => openEdit(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  openEdit(row)
                }
              }}
            >
              <div className="admin-mobile-record__head">
                <div className="admin-mobile-record__lead">
                  <BadgeThumb path={row.icon_path} name={row.name} />
                  <div className="admin-mobile-record__title">
                    {row.name}
                    <div className="admin-mobile-record__meta badges-mobile-card__code">
                      {row.code}
                    </div>
                  </div>
                </div>
                <Tag color={row.is_active ? 'green' : 'default'}>
                  {row.is_active ? '启用' : '禁用'}
                </Tag>
              </div>
              <div className="admin-mobile-record__body badges-mobile-card__desc">
                {row.description}
              </div>
              <div className="admin-mobile-record__meta">
                <span>{ELIGIBILITY_KIND_LABELS[row.eligibility_kind]}</span>
                <span>排序 {row.sort_order}</span>
                <span>
                  更新 {formatCST(row.updated_at, 'YYYY-MM-DD HH:mm')}
                </span>
              </div>
              <AdminRecordActions
                leading={
                  <Link
                    to="/audit"
                    search={{
                      target_type: 'badge_definition',
                      target_id: row.id,
                    }}
                  >
                    审计
                  </Link>
                }
                actions={[
                  {
                    key: 'toggle',
                    label: row.is_active ? '禁用' : '启用',
                    danger: row.is_active,
                    onClick: () => {
                      setActiveReason('')
                      setActiveTarget({ row, isActive: !row.is_active })
                    },
                  },
                  {
                    key: 'edit',
                    label: '编辑',
                    icon: <EditOutlined />,
                    onClick: () => openEdit(row),
                  },
                ]}
              />
            </article>
          )}
        />
      </Card>

      <Drawer
        title={editing === 'new' ? '新建徽章' : '编辑徽章'}
        open={editing !== null}
        width={isMobile ? '100%' : 480}
        destroyOnHidden
        onClose={() => {
          setEditing(null)
          setIconValue(null)
        }}
        footer={
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button
              onClick={() => {
                setEditing(null)
                setIconValue(null)
              }}
            >
              取消
            </Button>
            <Button
              type="primary"
              loading={saveMutation.isPending}
              onClick={() => form.submit()}
            >
              保存
            </Button>
          </Space>
        }
      >
        {editing === 'new' ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            title="新建后为禁用状态"
            description="upsert 不会同时启用。保存成功后请单独走启用流程，启用前会检查图标能否公开读取。"
          />
        ) : null}
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => saveMutation.mutate(values)}
        >
          <Form.Item
            name="code"
            label="代码"
            extra={
              editing === 'new'
                ? '小写字母开头，仅字母数字和下划线，最长 64。创建后不可改。'
                : '代码创建后只读。'
            }
            rules={
              editing === 'new'
                ? [
                    { required: true, message: '请填写徽章代码' },
                    {
                      pattern: /^[a-z][a-z0-9_]{1,63}$/,
                      message: '代码格式无效',
                    },
                  ]
                : undefined
            }
          >
            <Input
              disabled={editing !== 'new'}
              placeholder="question_contributor"
              maxLength={64}
            />
          </Form.Item>
          <Form.Item label="资格类型">
            <Tag>
              {editing === 'new'
                ? ELIGIBILITY_KIND_LABELS.manual
                : editing
                  ? ELIGIBILITY_KIND_LABELS[editing.eligibility_kind]
                  : ELIGIBILITY_KIND_LABELS.manual}
            </Tag>
            <Typography.Paragraph
              type="secondary"
              style={{ margin: '8px 0 0' }}
            >
              普通新建固定为人工授予。永久会员徽章复用预置条目，不能再创建一枚。
            </Typography.Paragraph>
          </Form.Item>
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请填写徽章名称' }]}
          >
            <Input maxLength={64} showCount />
          </Form.Item>
          <Form.Item
            name="description"
            label="说明"
            rules={[{ required: true, message: '请填写徽章说明' }]}
          >
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
          <Form.Item
            label="图标"
            required
            validateStatus={iconValue ? undefined : 'error'}
            help={
              pathForSavedBadgeIcon(iconValue) || iconValue?.kind === 'local'
                ? undefined
                : '请上传 96×96 PNG 或 WebP'
            }
          >
            <BadgeIconUpload
              value={iconValue}
              onChange={setIconValue}
              disabled={saveMutation.isPending}
            />
          </Form.Item>
          <Form.Item
            name="sort_order"
            label="排序"
            tooltip="数字越小越靠前"
            rules={[{ required: true, message: '请填写排序' }]}
          >
            <InputNumber min={0} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="reason"
            label="操作原因"
            rules={[
              { required: true, message: '请填写操作原因' },
              {
                validator: async (_, value: string) => {
                  if (!value || !value.trim()) {
                    throw new Error('请填写操作原因')
                  }
                },
              },
            ]}
          >
            <Input.TextArea rows={2} placeholder="将写入审计日志" />
          </Form.Item>
        </Form>
      </Drawer>

      <Modal
        title={activeTarget?.isActive ? '启用徽章' : '禁用徽章'}
        open={activeTarget !== null}
        okText={activeTarget?.isActive ? '启用' : '禁用'}
        okButtonProps={{
          danger: activeTarget?.isActive === false,
          icon: activeTarget?.isActive ? undefined : <StopOutlined />,
        }}
        confirmLoading={activeMutation.isPending}
        onCancel={() => {
          if (activeMutation.isPending) return
          setActiveTarget(null)
          setActiveReason('')
        }}
        onOk={() => {
          if (!activeTarget) return
          if (!activeReason.trim()) {
            message.error('请填写操作原因')
            return
          }
          activeMutation.mutate({
            row: activeTarget.row,
            isActive: activeTarget.isActive,
            reason: activeReason,
          })
        }}
        destroyOnHidden
      >
        {activeTarget && !activeTarget.isActive ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            title="禁用不会撤销已有授予"
            description="禁用后所有用户都无法展示此徽章，授予记录及佩戴偏好会保留；再次启用后，仍有资格的用户可恢复原佩戴。已撤销或已到期的资格不会因为重新启用而恢复。"
          />
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            title="启用前会检查图标"
            description="数据库只校验路径语法。管理端会确认对象可读取、可解码且为 96×96。占位路径需要先替换真实图标。"
          />
        )}
        {activeTarget ? (
          <Typography.Paragraph>
            目标：{activeTarget.row.name}（{activeTarget.row.code}）
          </Typography.Paragraph>
        ) : null}
        <Input.TextArea
          rows={3}
          value={activeReason}
          onChange={(event) => setActiveReason(event.target.value)}
          placeholder="操作原因（必填）"
        />
      </Modal>
    </Space>
  )
}

function BadgeThumb({ path, name }: { path: string; name: string }) {
  const url = badgeIconPublicUrl(path)
  if (!url) {
    return (
      <div
        aria-hidden
        style={{
          width: 32,
          height: 32,
          borderRadius: 6,
          background: 'var(--ant-color-fill-secondary)',
        }}
      />
    )
  }
  return (
    <Image
      src={url}
      alt={name}
      width={32}
      height={32}
      preview={false}
      style={{ borderRadius: 6, objectFit: 'contain' }}
      fallback="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32'></svg>"
    />
  )
}
